import { scaleLinear, ScaleLinear } from 'd3';
import {
  StageSubmissionFragment,
  VoteForSubmissionData,
  VoteForViewConfig,
  VoteForViewData,
} from '@/generated/graphql';
import {
  CSSProperties,
  RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import elasticStyles from '@/styles/ElasticTransition.module.css';
import { useImageLoader } from '@/hooks/useImageLoader';
import { Loader } from '@/components/Loader';
import { useStageEngagementSubmissions } from '@/hooks/useStageEngagementSubmissions';
import { createSubmissionTypeFilter } from '@/utils/createSubmissionTypeFilter';
import { keyBy } from 'lodash';
import { cn } from '@/lib/utils';
import { computeVoteCounts } from '../utils';
import { TypedStageEngagement } from '@/utils/engagementTypeUtils';
import {
  availableBubbleHeight,
  BUBBLE_NEIGHBOR_GAP_PX,
  computeBubbleTextFit,
  computeMaxCenteredBubbleWidth,
  computeMaxVoteBarRange,
  computeWinnerColumnZoom,
  DESIRED_WINNER_SCALE,
  FitRect,
  MAX_WINNER_BUBBLE_LINES,
  minWinnerTextScale,
  rotationHeightPad,
  stageEngagementAreaRect,
  winnerChoiceTransform,
  winnerTitleTransform,
  zoomedFullWinnerFontPx,
  zoomedY,
} from './winnerFit';

const voteForSubmissionTypeFilter =
  createSubmissionTypeFilter<VoteForSubmissionData>('VoteForSubmissionData');

export function StageVoteForEngagement({
  engagement,
}: {
  engagement: TypedStageEngagement<
    VoteForViewConfig,
    VoteForViewData,
    VoteForSubmissionData
  >;
}) {
  const { data, loading, error } =
    useStageEngagementSubmissions<VoteForSubmissionData>({
      engagementId: engagement.id,
    });

  const choiceDisplayOrder = engagement.viewData?.choiceDisplayOrder;

  const sortedChoices = useMemo(() => {
    if (!data?.submissions) return [];
    const submissionsById = keyBy(data.submissions, 'id');
    return (choiceDisplayOrder || [])
      .map((id) => submissionsById[id])
      .filter(Boolean)
      .filter(voteForSubmissionTypeFilter);
  }, [choiceDisplayOrder, data?.submissions]);

  const voteCounts = useMemo(
    () => computeVoteCounts(engagement.viewData),
    [engagement.viewData],
  );

  return (
    <>
      {loading && <Loader />}
      {error && <div>Error: {error.message}</div>}
      <VoteForStageBoard
        choices={sortedChoices}
        voteCounts={voteCounts}
        winners={engagement.viewData?.voteWinners}
      />
    </>
  );
}

export function VoteForStageBoard({
  choices,
  voteCounts,
  winners,
}: {
  choices: StageSubmissionFragment[];
  voteCounts: Record<string, number>;
  winners?: string[] | null;
}) {
  const voteContainerRef = useRef<HTMLDivElement>(null);
  const [choiceWidth, setChoiceWidth] = useState(0);
  const [voteScale, setVoteScale] = useState<ScaleLinear<
    number,
    number
  > | null>(null);

  useLayoutEffect(() => {
    if (!choices.length || !voteContainerRef.current) return;
    const container = voteContainerRef.current;
    const newChoiceWidth = Math.min(
      (container.clientWidth / choices.length) * 0.8,
      container.clientWidth / 8,
    );
    const maxVotes = Math.max(10, choices.length, ...Object.values(voteCounts));
    const naiveMax = container.clientHeight ? container.clientHeight * 0.6 : 0;
    setChoiceWidth(newChoiceWidth);
    setVoteScale(() => scaleLinear([0, maxVotes], [0, naiveMax]));
  }, [choices.length, voteCounts]);

  useLayoutEffect(() => {
    const container = voteContainerRef.current;
    if (
      !container ||
      !voteScale ||
      !choiceWidth ||
      !winners?.length ||
      !choices.length
    ) {
      return;
    }
    const maxVotes = Math.max(10, choices.length, ...Object.values(voteCounts));
    const naiveMax = container.clientHeight ? container.clientHeight * 0.6 : 0;
    const nextMax = measureReservedBarRange({
      container,
      voteCounts,
      maxVotes,
      naiveMax,
    });
    if (nextMax == null) return;
    const currentMax = voteScale.range()[1];
    if (Math.abs(currentMax - nextMax) < 0.5) return;
    setVoteScale(() => scaleLinear([0, maxVotes], [0, nextMax]));
  }, [choiceWidth, choices.length, voteCounts, voteScale, winners]);

  return (
    <div
      ref={voteContainerRef}
      data-name="VOTE-FOR-CONTAINER"
      className="flex h-full w-full flex-col items-end justify-around overflow-visible"
    >
      {/* This div creates the space between the choices container and the top of the vote for container */}
      <div data-name="CHOICE-RANGE-REF" className="w-full flex-1" />
      <div
        data-name="CHOICES_CONTAINER"
        className={`flex w-full flex-row justify-around`}
      >
        {choices.map((choice) => (
          <Choice
            key={choice.id}
            choice={choice}
            votes={voteCounts[choice.id] || 0}
            isWinner={Boolean(winners?.includes(choice.id))}
            isLoser={Boolean(winners?.length && !winners.includes(choice.id))}
            handleClick={() => {}}
            width={choiceWidth}
            voteScale={voteScale}
            viewportRef={voteContainerRef}
          />
        ))}
      </div>
    </div>
  );
}

function rectFromDom(el: Element | null | undefined): FitRect | null {
  if (!el) return null;
  const { top, right, bottom, left } = el.getBoundingClientRect();
  return { top, right, bottom, left };
}

function renderedFontPx(el: Element | null): number {
  if (!el) return 0;
  const fontSize = parseFloat(window.getComputedStyle(el).fontSize);
  if (!Number.isFinite(fontSize) || fontSize <= 0) return 0;
  let scale = 1;
  let node: Element | null = el;
  while (node) {
    const transform = window.getComputedStyle(node).transform;
    if (transform && transform !== 'none') {
      try {
        const matrix = new DOMMatrixReadOnly(transform);
        scale *= Math.hypot(matrix.a, matrix.b);
      } catch {
        // ignore
      }
    }
    node = node.parentElement;
  }
  return fontSize * scale;
}

function readLiftPx(wrapper: HTMLElement | null): number {
  if (!wrapper) return 0;
  const top = parseFloat(wrapper.style.top);
  return Number.isFinite(top) ? Math.abs(top) : 0;
}

function measureReservedBarRange({
  container,
  voteCounts,
  maxVotes,
  naiveMax,
}: {
  container: HTMLElement;
  voteCounts: Record<string, number>;
  maxVotes: number;
  naiveMax: number;
}): number | null {
  const winnerNodes = [
    ...container.querySelectorAll<HTMLElement>(
      '[data-name="CHOICE_CONTAINER"][data-winner="true"]',
    ),
  ];
  if (winnerNodes.length === 0) return null;

  const area = stageEngagementAreaRect(window.innerWidth, window.innerHeight);
  let rangeMax = naiveMax;

  for (const choice of winnerNodes) {
    const previousTransform = choice.style.transform;
    const previousTransition = choice.style.transition;
    const text = choice.querySelector(
      '[data-name="CHOICE_TITLE_TEXT"]',
    ) as HTMLElement | null;
    const title = choice.querySelector(
      '[data-name="CHOICE_TITLE"]',
    ) as HTMLElement | null;
    const star = choice.querySelector('[data-name="WINNER_STAR"]');
    const avatar = choice.querySelector(
      '[data-name="CHOICE_IMAGE_WRAPPER"]',
    ) as HTMLElement | null;

    choice.style.transition = 'none';
    choice.style.transform = 'none';
    applyBubbleTextDom(text, {
      textScale: 1,
      maxLines: MAX_WINNER_BUBBLE_LINES,
    });
    void choice.offsetWidth;

    const choiceRect = rectFromDom(choice);
    const avatarRect = rectFromDom(avatar);
    const titleRect = rectFromDom(title);
    const starRect = rectFromDom(star);
    const lineHeight = text ? readLineHeight(text) : 16;
    const bubbleHeight = Math.min(
      text?.offsetHeight || title?.offsetHeight || lineHeight,
      lineHeight * MAX_WINNER_BUBBLE_LINES,
    );
    const starOverhang =
      starRect && titleRect ? Math.max(0, titleRect.top - starRect.top) : 0;
    const reservedStackPx =
      bubbleHeight + starOverhang + rotationHeightPad(title?.offsetWidth ?? 0);
    const lift = readLiftPx(avatar);
    const columnBodyPx =
      choiceRect && avatarRect
        ? Math.max(0, choiceRect.bottom - avatarRect.top - lift)
        : 0;

    choice.style.transform = previousTransform;
    choice.style.transition = previousTransition;

    if (!choiceRect || !avatarRect || columnBodyPx < 2) continue;

    const id = choice.getAttribute('data-choice-id') || '';
    const winnerVotes = voteCounts[id] ?? 0;
    rangeMax = Math.min(
      rangeMax,
      computeMaxVoteBarRange({
        originY: choiceRect.bottom,
        areaTop: area.top,
        columnBodyPx,
        reservedStackPx,
        winnerVotes,
        maxVotes,
        zoom: DESIRED_WINNER_SCALE,
        naiveMax,
      }),
    );
  }

  return rangeMax;
}

function readLineHeight(el: Element): number {
  const cs = window.getComputedStyle(el);
  const lineHeight = parseFloat(cs.lineHeight);
  if (Number.isFinite(lineHeight) && lineHeight > 0) return lineHeight;
  const fontSize = parseFloat(cs.fontSize);
  return Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 16;
}

function applyBubbleTextDom(
  text: HTMLElement | null,
  fit: { textScale: number; maxLines: number | null },
) {
  if (!text) return;
  text.style.fontSize = fit.textScale === 1 ? '' : `${fit.textScale}em`;
  if (fit.maxLines == null) {
    text.style.display = '';
    text.style.webkitBoxOrient = '';
    text.style.webkitLineClamp = '';
    text.style.overflow = '';
  } else {
    text.style.display = '-webkit-box';
    text.style.webkitBoxOrient = 'vertical';
    text.style.webkitLineClamp = String(fit.maxLines);
    text.style.overflow = 'hidden';
  }
}

type WinnerFitStyle = {
  column: CSSProperties;
  textScale: number;
  maxLines: number | null;
  bubbleWidth: number | null;
};

const EMPTY_WINNER_FIT: WinnerFitStyle = {
  column: {},
  textScale: 1,
  maxLines: null,
  bubbleWidth: null,
};

function textOverflowsLines(
  text: HTMLElement,
  lineHeight: number,
  lines: number,
) {
  return text.scrollHeight > lineHeight * lines + 1;
}

function neighborLimits(choice: HTMLElement): {
  leftObstacle: number;
  rightObstacle: number;
} {
  const parent = choice.parentElement;
  const siblings = parent
    ? ([
        ...parent.querySelectorAll('[data-name="CHOICE_CONTAINER"]'),
      ] as HTMLElement[])
    : [];
  const self = choice.getBoundingClientRect();
  let leftObstacle = 0;
  let rightObstacle = window.innerWidth;
  for (const other of siblings) {
    if (other === choice) continue;
    const rect = other.getBoundingClientRect();
    if (rect.right <= self.left + 1 && rect.right > leftObstacle) {
      leftObstacle = rect.right;
    }
    if (rect.left >= self.right - 1 && rect.left < rightObstacle) {
      rightObstacle = rect.left;
    }
  }
  return { leftObstacle, rightObstacle };
}

function fitWinnerBubbleWidth({
  title,
  text,
  avatarWidth,
  avatarCenter,
  leftObstacle,
  rightObstacle,
  starHalfWidth,
  zoom,
}: {
  title: HTMLElement;
  text: HTMLElement;
  avatarWidth: number;
  avatarCenter: number;
  leftObstacle: number;
  rightObstacle: number;
  starHalfWidth: number;
  zoom: number;
}): number {
  const maxWidth = computeMaxCenteredBubbleWidth({
    avatarWidth,
    avatarCenter,
    leftObstacle,
    rightObstacle,
    extraLeft: starHalfWidth,
    gap: BUBBLE_NEIGHBOR_GAP_PX,
    zoom,
  });
  title.style.width = `${avatarWidth}px`;
  void title.offsetWidth;
  const lineHeight = readLineHeight(text);
  applyBubbleTextDom(text, {
    textScale: 1,
    maxLines: MAX_WINNER_BUBBLE_LINES,
  });
  void text.offsetWidth;
  if (!textOverflowsLines(text, lineHeight, MAX_WINNER_BUBBLE_LINES)) {
    return avatarWidth;
  }
  let lo = avatarWidth;
  let hi = maxWidth;
  for (let i = 0; i < 12; i += 1) {
    const mid = (lo + hi) / 2;
    title.style.width = `${mid}px`;
    void title.offsetWidth;
    if (textOverflowsLines(text, lineHeight, MAX_WINNER_BUBBLE_LINES)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  title.style.width = `${hi}px`;
  return Math.round(hi * 1000) / 1000;
}

function measureWinnerFit(choice: HTMLElement): WinnerFitStyle | null {
  const previousTransform = choice.style.transform;
  const previousTransition = choice.style.transition;
  const text = choice.querySelector(
    '[data-name="CHOICE_TITLE_TEXT"]',
  ) as HTMLElement | null;
  const title = choice.querySelector(
    '[data-name="CHOICE_TITLE"]',
  ) as HTMLElement | null;
  const star = choice.querySelector('[data-name="WINNER_STAR"]');
  const avatar =
    choice.querySelector('[data-name="CHOICE_IMAGE_WRAPPER"]') ||
    choice.querySelector('[data-name="CHOICE_IMAGE"]');

  choice.style.transition = 'none';
  choice.style.transform = 'none';
  applyBubbleTextDom(text, { textScale: 1, maxLines: null });
  void choice.offsetWidth;

  const choiceRect = rectFromDom(choice);
  const avatarRect = rectFromDom(avatar);
  const titleRect = rectFromDom(title);
  const starRect = rectFromDom(star);

  if (!choiceRect || !avatarRect || choiceRect.right - choiceRect.left < 2) {
    choice.style.transform = previousTransform;
    choice.style.transition = previousTransition;
    return null;
  }

  const originY = choiceRect.bottom;
  const area = stageEngagementAreaRect(window.innerWidth, window.innerHeight);
  const starOverhang =
    starRect && titleRect ? Math.max(0, titleRect.top - starRect.top) : 0;
  const lineHeight = text ? readLineHeight(text) : 16;
  const avatarWidth = avatarRect.right - avatarRect.left;
  const avatarCenter = (avatarRect.left + avatarRect.right) / 2;
  const minStackPx =
    lineHeight * MAX_WINNER_BUBBLE_LINES +
    starOverhang +
    rotationHeightPad(avatarWidth);
  const zoom = computeWinnerColumnZoom({
    unscaledAvatarTop: avatarRect.top,
    originY,
    areaTop: area.top,
    minStackPx,
  });

  const { leftObstacle, rightObstacle } = neighborLimits(choice);
  const starHalfWidth = starRect ? (starRect.right - starRect.left) / 2 : 0;
  let bubbleWidth = avatarWidth;
  if (title && text) {
    bubbleWidth = fitWinnerBubbleWidth({
      title,
      text,
      avatarWidth,
      avatarCenter,
      leftObstacle,
      rightObstacle,
      starHalfWidth,
      zoom,
    });
  }

  const layoutHeight = text?.offsetHeight || title?.offsetHeight || 0;
  const zoomedAvatarTop = zoomedY({
    unscaledY: avatarRect.top,
    originY,
    zoom,
  });
  const availableHeight = Math.max(
    0,
    availableBubbleHeight({
      zoomedAvatarTop,
      areaTop: area.top,
      zoomedStarOverhang: starOverhang * zoom,
    }) -
      rotationHeightPad(title?.offsetWidth ?? bubbleWidth) * zoom,
  );
  const viewport = choice.closest('[data-name="VOTE-FOR-CONTAINER"]');
  const loserFonts = viewport
    ? [
        ...viewport.querySelectorAll(
          '[data-name="CHOICE_CONTAINER"][data-winner="false"] [data-name="CHOICE_TITLE_TEXT"]',
        ),
      ].map((el) => renderedFontPx(el))
    : [];
  const loserRenderedFontPx = loserFonts.length ? Math.max(...loserFonts) : 0;
  const winnerCssFontPx = text
    ? parseFloat(window.getComputedStyle(text).fontSize) || 0
    : 0;
  const zoomedFullFontPx = zoomedFullWinnerFontPx({
    viewportWidth: window.innerWidth,
    columnZoom: zoom,
  });
  const minTextScale = minWinnerTextScale({
    cssFontPx: winnerCssFontPx,
    zoomedFullFontPx,
    loserRenderedFontPx,
    columnZoom: zoom,
  });
  const fit = computeBubbleTextFit({
    availableHeight,
    naturalHeight: layoutHeight * zoom,
    lineHeight: lineHeight * zoom,
    minTextScale,
  });

  const result: WinnerFitStyle = {
    column: {
      transform: winnerChoiceTransform(zoom),
      transformOrigin: '50% 100%',
    },
    textScale: fit.textScale,
    maxLines: fit.maxLines ?? MAX_WINNER_BUBBLE_LINES,
    bubbleWidth,
  };
  choice.style.transform = result.column.transform as string;
  choice.style.transformOrigin = '50% 100%';
  choice.style.transition = previousTransition;
  applyBubbleTextDom(text, result);
  return result;
}

function useWinnerFitStyle({
  enabled,
  choiceRef,
  viewportRef,
  layoutKey,
}: {
  enabled: boolean;
  choiceRef: RefObject<HTMLElement>;
  viewportRef: RefObject<HTMLElement>;
  layoutKey: string;
}): WinnerFitStyle {
  const [style, setStyle] = useState<WinnerFitStyle>(EMPTY_WINNER_FIT);

  useLayoutEffect(() => {
    const choice = choiceRef.current;
    const viewport = viewportRef.current;
    if (!enabled || !choice || !viewport) {
      setStyle((prev) => (prev === EMPTY_WINNER_FIT ? prev : EMPTY_WINNER_FIT));
      return;
    }

    let raf = 0;
    const update = () => {
      const next = measureWinnerFit(choice);
      if (!next) return;
      setStyle((prev) => {
        if (
          prev.column.transform === next.column.transform &&
          prev.textScale === next.textScale &&
          prev.maxLines === next.maxLines &&
          prev.bubbleWidth === next.bubbleWidth
        ) {
          return prev;
        }
        return next;
      });
    };
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };

    update();
    const observer = new ResizeObserver(schedule);
    observer.observe(viewport);
    observer.observe(document.documentElement);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [choiceRef, enabled, layoutKey, viewportRef]);

  return style;
}

function bubbleTextStyle(fit: WinnerFitStyle): CSSProperties | undefined {
  if (fit.textScale === 1 && fit.maxLines == null) return undefined;
  const style: CSSProperties = {};
  if (fit.textScale !== 1) {
    style.fontSize = `${fit.textScale}em`;
  }
  if (fit.maxLines != null) {
    style.display = '-webkit-box';
    style.WebkitBoxOrient = 'vertical';
    style.WebkitLineClamp = fit.maxLines;
    style.overflow = 'hidden';
    style.wordBreak = 'break-word';
  }
  return style;
}

const Choice = ({
  votes,
  choice,
  isWinner,
  isLoser,
  handleClick,
  width,
  voteScale,
  viewportRef,
}: {
  votes: number;
  choice: StageSubmissionFragment;
  isWinner?: boolean;
  isLoser?: boolean;
  handleClick: (choice: StageSubmissionFragment) => void;
  width: number;
  voteScale?: ScaleLinear<number, number> | null;
  viewportRef: RefObject<HTMLDivElement>;
}) => {
  const choiceRef = useRef<HTMLDivElement>(null);
  const data = (choice.data || {}) as VoteForSubmissionData;
  const { imageLoaded, url } = useImageLoader({
    url: data.optionalImageUrl ?? undefined,
  });
  const loaded = data.optionalImageUrl ? imageLoaded : true;
  const winnerFitStyle = useWinnerFitStyle({
    enabled: Boolean(isWinner),
    choiceRef,
    viewportRef,
    layoutKey: `${width}:${votes}:${loaded}:${data.title}:${isWinner ? '1' : '0'}`,
  });
  const columnStyle = isWinner
    ? winnerFitStyle.column
    : { transformOrigin: '50% 100%' };
  const widthScale = useMemo(
    () =>
      voteScale
        ? voteScale.copy().range([4, 10])
        : scaleLinear([0, 10], [4, 10]),
    [voteScale],
  );
  const { voteStyles, voteBarStyles, imageStyles, imageWrapperStyles } =
    useMemo(() => {
      const voteSize = width / 2;

      const voteHeight = loaded && voteScale ? voteScale?.(votes) : 0;
      return {
        voteStyles: {
          width: voteSize,
          height: voteSize,
          boxShadow: `0 0 0 ${widthScale(votes)}px ${data.color}`,
        },
        voteBarStyles: {
          height: `${voteHeight + voteSize * 2}px`,
          bottom: 0,
          backgroundColor: data.color,
          width: voteSize,
          borderRadius: `${voteSize / 2}px`,
        },
        imageStyles: {
          border: `0.3vw solid white`,
          width,
          height: width,
          backgroundColor: data.color,
        },
        imageWrapperStyles: {
          top: `-${voteHeight}px`,
          backgroundColor: 'black',
          borderColor: data.color,
          borderWidth: `0.3vw`,
        },
      };
    }, [width, voteScale, votes, widthScale, data.color, loaded]);

  return (
    <div
      ref={choiceRef}
      data-name="CHOICE_CONTAINER"
      data-winner={isWinner ? 'true' : 'false'}
      data-choice-id={choice.id}
      key={choice.id}
      className={cn(
        `relative flex flex-col items-center justify-center overflow-visible transition-all ${loaded ? `opacity-100` : 'opacity-20'} ${isLoser ? 'scale-95 opacity-50' : ''}`,
      )}
      style={columnStyle}
      data-column-zoom={
        isWinner ? (winnerFitStyle.column.transform ?? 'scale(1)') : undefined
      }
      data-font-scale={isWinner ? String(winnerFitStyle.textScale) : undefined}
      data-bubble-ratio={
        isWinner && winnerFitStyle.bubbleWidth
          ? String(
              Math.round((winnerFitStyle.bubbleWidth / width) * 1000) / 1000,
            )
          : undefined
      }
      data-max-lines={
        isWinner
          ? winnerFitStyle.maxLines == null
            ? ''
            : String(winnerFitStyle.maxLines)
          : undefined
      }
      onClick={() => handleClick(choice)}
    >
      <div
        data-name="VOTE-BAR"
        className={`absolute ${elasticStyles.elastic} `}
        style={voteBarStyles}
      />

      <div
        data-name="CHOICE_IMAGE_WRAPPER"
        style={imageWrapperStyles}
        className={`relative flex items-center justify-center overflow-visible rounded-full border-solid ${elasticStyles.elastic} `}
      >
        <div
          data-name="CHOICE_TITLE"
          style={{
            boxShadow: isWinner
              ? `-1px 1px 0 4px ${data.color}`
              : `3px -3px 0 ${data.color}`,
            width:
              isWinner && winnerFitStyle.bubbleWidth
                ? winnerFitStyle.bubbleWidth
                : width,
            transform: isWinner ? winnerTitleTransform() : 'none',
          }}
          className={cn(
            'absolute bottom-full z-10',
            'px-2 py-0',
            'rounded-lg bg-white font-screen-body',
            'text-center leading-none text-black',
            'transition-all',
            {
              'text-[1.5vw]': isWinner || data.title.length <= 10,
              'text-[1.2vw]': !isWinner && data.title.length > 10,
            },
          )}
        >
          {isWinner && (
            <div
              data-name="WINNER_STAR"
              className="absolute bottom-full left-0 z-20 -translate-x-1/2 translate-y-1/2 items-center gap-1 rounded-full px-2 text-[1.2vw] text-epic-foreground"
            >
              ⭐️
            </div>
          )}
          <span
            data-name="CHOICE_TITLE_TEXT"
            className="w-full"
            style={isWinner ? bubbleTextStyle(winnerFitStyle) : undefined}
          >
            {data.title}
          </span>
        </div>
        {!loaded && (
          <Loader
            className={`absolute flex h-full w-full items-center justify-center`}
          />
        )}
        {url ? (
          <img
            data-name="CHOICE_IMAGE"
            src={url}
            style={imageStyles}
            className={`rounded-full transition-opacity ${loaded ? 'opacity-100' : `opacity-0`} `}
          />
        ) : (
          <div
            data-name="CHOICE_IMAGE"
            style={imageStyles}
            className={`rounded-full transition-opacity ${loaded ? 'opacity-100' : `opacity-0`} `}
          ></div>
        )}
      </div>

      <div
        data-name="CHOICE_VOTE_COUNT"
        style={voteStyles}
        className={`relative z-10 flex items-center justify-center rounded-full ${elasticStyles.elastic} bg-white text-center font-screen-heading text-[2.8vw] text-black`}
      >
        {votes}
      </div>
    </div>
  );
};
